import { GRAVIDADE_M_S2 } from '../dominio/tabletop/estatica'
import type { CurvaDeForca, PontoCurvaForca } from '../dominio/tabletop/forcaDeChoque'
import type { PontoDeImpacto } from '../dominio/tabletop/impactoQueda'
import type { VetorTabletop } from '../dominio/tabletop/tipos'
import type { Rapier } from './motor'

/**
 * A queda de um trabalhador preso por um talabarte, simulada com o motor de
 * física: uma massa pontual no engate, em queda livre e depois presa a uma
 * corda que só puxa.
 *
 * O motor cuida do corpo rígido, da gravidade e da integração; a corda é uma
 * força aplicada a cada passo, no mesmo modelo do cálculo da força de choque
 * (`calcularForcaDeChoque`): uma mola de rigidez `k = EA / L` em série com um
 * freio de força constante (o absorvedor, o Prusik ou o freio dinâmico).
 *
 *   comprimento  ℓ = L + x + d      (x: a mola esticada; d: o freio escorregado)
 *   tensão       T = k·x            enquanto T ≤ F_freio
 *   escorrega    T = F_freio, d cresce até a abertura máxima (nunca volta)
 *
 * Diferente do cálculo, que mantém a queda na vertical, aqui a força da corda
 * aponta para a ancoragem: com a ancoragem deslocada para o lado o corpo
 * balança em arco enquanto freia, e o pêndulo sai da dinâmica, sem fórmula.
 * Na vertical, o pico simulado tem de bater com o calculado.
 *
 * A corda guarda a energia ao carregar e devolve só parte dela ao descarregar
 * (histerese): sem isso a fita voltaria quase à altura de onde caiu. O
 * balanço é amortecido depois da frenagem (arrasto e atrito, didáticos).
 *
 * Quem bate em quê vem de fora (`detectar`): o mesmo critério da queda
 * calculada, para as duas verem a mesma cena.
 */

export type FaseSimulada = 'queda-livre' | 'frenagem' | 'pendulo' | 'parado' | 'rompido'

export interface RompimentoSimulado {
  readonly t: number
  readonly forcaN: number
  readonly quadro: number
  readonly alturaM?: number
  readonly cargaRupturaN?: number
  readonly velocidadeImpactoMs?: number
}

export interface EntradaSimulacaoQueda {
  readonly ancoragem?: VetorTabletop | null
  /** O engate, em repouso, no instante em que o trabalhador perde o apoio. */
  readonly partida: VetorTabletop
  readonly massaKg: number
  readonly comprimentoM?: number
  /** Rigidez do talabarte (EA), em kN: a força que o esticaria 100%. */
  readonly rigidezEAkN?: number
  /** Força de frenagem do redutor, em N. */
  readonly forcaRedutorN?: number
  /** Quanto o redutor escorrega no máximo; 0 é sem redutor. */
  readonly aberturaMaximaM?: number
  /** Limite de carga para ruptura do talabarte, em N (ex: 22000 N, ou 4000 N para fita desgastada). */
  readonly cargaRupturaN?: number
  /** Amortecimento do balanço depois da frenagem, por segundo (a amplitude cai à metade disso). */
  readonly amortecimentoPendulo?: number
  /** Razão de amortecimento da corda ao descarregar; 0 é uma mola perfeita. */
  readonly histerese?: number
  readonly duracaoS?: number
  readonly passoS?: number
}

export interface QuadroSimulacaoQueda {
  readonly t: number
  readonly engate: VetorTabletop
  readonly forcaN: number
  readonly fase: FaseSimulada
}

export interface ImpactoSimulado extends PontoDeImpacto {
  /** Índice do último quadro (o do impacto). */
  readonly quadro: number
  readonly t: number
  readonly velocidadeMs: number
  readonly fase: FaseSimulada
}

export interface ResultadoSimulacaoQueda {
  readonly quadros: readonly QuadroSimulacaoQueda[]
  /** Maior força na corda durante toda a simulação, em N. */
  readonly picoForcaN: number
  /** Quando o talabarte esticou pela primeira vez; `null` se nunca esticou. */
  readonly tEsticouS: number | null
  readonly engateAoEsticar: VetorTabletop | null
  readonly velocidadeAoEsticarMs: number
  /** Quando acabou o primeiro carregamento (o corpo parou de se afastar da ancoragem). */
  readonly tFimDaFrenagemS: number | null
  readonly engateNoFimDaFrenagem: VetorTabletop | null
  /** Quanto a mola estava esticada no pico. */
  readonly alongamentoNoPicoM: number
  /** Quanto o redutor escorregou. */
  readonly aberturaUsadaM: number
  /** Do esticar ao ponto mais baixo da frenagem: mola e redutor juntos. */
  readonly descidaAposEsticarM: number
  readonly curva: CurvaDeForca | null
  readonly impacto: ImpactoSimulado | null
  /** Ocorrência de ruptura do talabarte por sobrecarga ou desgaste. */
  readonly rompimento: RompimentoSimulado | null
  /** O balanço terminou: o corpo ficou pendurado, parado. */
  readonly assentou: boolean
  readonly rigidezNPorM: number
}

export const PASSO_PADRAO_S = 1 / 2000
const QUADROS_POR_SEGUNDO = 30
/** A cada quantos passos o detector de impacto olha o corpo (a 2 kHz, 8 passos são 4 ms). */
const PASSOS_ENTRE_DETECCOES = 8
/** O amortecimento é na velocidade: a amplitude do balanço cai a `c / 2` por segundo, então 0,8 dá cerca de três oscilações até parar. */
const AMORTECIMENTO_PENDULO_PADRAO = 0.8
const HISTERESE_PADRAO = 0.7
/** O arrasto só entra depois da frenagem: durante ela, a energia da queda é a do cálculo. */
const ATRASO_DO_AMORTECIMENTO_S = 0.5
const DURACAO_PADRAO_S = 15
const PONTOS_NA_CURVA = 120
/** Parado: o desvio da vertical da ancoragem e a velocidade, abaixo destes valores. */
const DESVIO_PARADO_M = 0.02
const VELOCIDADE_PARADO_MS = 0.02
const TEMPO_MINIMO_ASSENTAR_S = 1
/** Parado de verdade: ele passa por velocidade zero no ponto de inversão de cada oscilação, então o repouso tem de durar. */
const REPOUSO_SUSTENTADO_S = 0.25
/** E a corda tem de estar segurando o peso: a força dentro desta fração do peso. */
const FORCA_NO_PESO = 0.03

/** Corta a curva a cerca de `alvo` pontos, sempre guardando o primeiro, o pico e o último. */
function reduzirCurva(pontos: readonly PontoCurvaForca[], alvo: number): readonly PontoCurvaForca[] {
  const passo = Math.max(1, Math.floor(pontos.length / alvo))
  const indicePico = pontos.reduce((melhor, p, i) => (p.forcaKN > pontos[melhor]!.forcaKN ? i : melhor), 0)
  return pontos.filter((_, i) => i % passo === 0 || i === indicePico || i === pontos.length - 1)
}

export function simularQueda(
  R: Rapier,
  entrada: EntradaSimulacaoQueda,
  detectar?: (anterior: VetorTabletop | null, atual: VetorTabletop) => PontoDeImpacto | null,
): ResultadoSimulacaoQueda {
  const { ancoragem: A, partida, massaKg: m, cargaRupturaN } = entrada
  const L = entrada.comprimentoM ?? 0
  const Dmax = entrada.aberturaMaximaM ?? 0
  const passoS = entrada.passoS ?? PASSO_PADRAO_S
  const duracao = entrada.duracaoS ?? DURACAO_PADRAO_S
  const amortecimento = entrada.amortecimentoPendulo ?? AMORTECIMENTO_PENDULO_PADRAO
  const rigidezEAkN = entrada.rigidezEAkN ?? 80
  const k = L > 0 ? (rigidezEAkN * 1000) / L : 0
  const Fa = entrada.forcaRedutorN ?? 0
  const c = k > 0 ? 2 * (entrada.histerese ?? HISTERESE_PADRAO) * Math.sqrt(k * m) : 0
  const temCorda = Boolean(A && L > 0)

  const mundo = new R.World({ x: 0, y: -GRAVIDADE_M_S2, z: 0 })
  mundo.timestep = passoS
  const corpo = mundo.createRigidBody(R.RigidBodyDesc.dynamic().setTranslation(partida[0], partida[1], partida[2]).setLinearDamping(0))
  mundo.createCollider(R.ColliderDesc.ball(0.1).setMass(m).setSensor(true), corpo)

  const quadros: QuadroSimulacaoQueda[] = []
  const bruto: PontoCurvaForca[] = []
  const passos = Math.round(duracao / passoS)
  const passosPorQuadro = Math.max(1, Math.round(1 / passoS / QUADROS_POR_SEGUNDO))
  /** A curva de força é amostrada bem mais fino que os quadros: o carregamento de uma fita dura menos de um décimo de segundo. */
  const passosPorAmostra = 2

  let d = 0
  let pico = 0
  let xNoPico = 0
  let tEsticou: number | null = null
  let engateAoEsticar: VetorTabletop | null = null
  let velocidadeAoEsticar = 0
  let tFim: number | null = null
  let engateFim: VetorTabletop | null = null
  let descida = 0
  let anteriorDetectado: VetorTabletop | null = null
  let impacto: ImpactoSimulado | null = null
  let rompimento: RompimentoSimulado | null = null
  let rompeu = false
  let assentou = false
  let amortecendo = false
  let paradoDesde: number | null = null
  const peso = m * GRAVIDADE_M_S2

  for (let i = 0; i <= passos; i += 1) {
    const t = i * passoS
    const p = corpo.translation()
    const v = corpo.linvel()
    const posicao: VetorTabletop = [p.x, p.y, p.z]

    let rx = 0
    let ry = 0
    let rz = 0
    let comprimento = 0
    let ux = 0
    let uy = 0
    let uz = 0
    let alongamentoPorS = 0

    if (temCorda && A && !rompeu) {
      rx = A[0] - p.x
      ry = A[1] - p.y
      rz = A[2] - p.z
      comprimento = Math.hypot(rx, ry, rz)
      ux = comprimento > 1e-9 ? rx / comprimento : 0
      uy = comprimento > 1e-9 ? ry / comprimento : 0
      uz = comprimento > 1e-9 ? rz / comprimento : 0
      alongamentoPorS = -(ux * v.x + uy * v.y + uz * v.z)
    }

    let x = temCorda && !rompeu ? comprimento - L - d : 0
    let T = 0
    if (temCorda && !rompeu && x > 0) {
      T = k * x
      // o redutor escorrega quando a mola chega à força dele, e não volta
      if (Dmax > 0 && T > Fa && d < Dmax) {
        d = Math.min(Dmax, Math.max(d, comprimento - L - Fa / k))
        x = comprimento - L - d
        T = Math.max(0, k * x)
      }
      // ao descarregar, a corda devolve só parte da energia
      if (alongamentoPorS < 0 && c > 0) T = Math.max(0, T + c * alongamentoPorS)

      // Verificação de ruptura mecânica da corda/talabarte
      if (cargaRupturaN !== undefined && T >= cargaRupturaN) {
        rompeu = true
        rompimento = { t, forcaN: T, quadro: quadros.length, alturaM: posicao[1], cargaRupturaN }
        T = 0
      }
    }

    if (tEsticou === null && T > 0) {
      tEsticou = t
      engateAoEsticar = posicao
      velocidadeAoEsticar = Math.hypot(v.x, v.y, v.z)
    }
    if (tEsticou !== null && tFim === null && t > tEsticou + 2 * passoS && alongamentoPorS <= 0 && !rompeu) {
      tFim = t
      engateFim = posicao
      descida = comprimento - L
      bruto.push({ t: t - tEsticou, forcaKN: T / 1000, descidaM: comprimento - L })
    }
    if (T > pico) {
      pico = T
      xNoPico = Math.max(0, x)
    }

    const fase: FaseSimulada = rompeu
      ? 'rompido'
      : tEsticou === null
        ? 'queda-livre'
        : tFim === null
          ? 'frenagem'
          : 'pendulo'

    if (tEsticou !== null && tFim === null && i % passosPorAmostra === 0 && !rompeu) {
      bruto.push({ t: t - tEsticou, forcaKN: T / 1000, descidaM: comprimento - L })
    }

    if (i % passosPorQuadro === 0) quadros.push({ t, engate: posicao, forcaN: T, fase })

    // bateu em algo? olha a cada poucos passos, e não no primeiro (o trabalhador sai da borda)
    if (detectar && i > 0 && i % PASSOS_ENTRE_DETECCOES === 0) {
      const batida = detectar(anteriorDetectado, posicao)
      anteriorDetectado = posicao
      if (batida) {
        if (i % passosPorQuadro !== 0) quadros.push({ t, engate: posicao, forcaN: T, fase })
        if (tEsticou !== null && tFim === null && !rompeu) bruto.push({ t: t - tEsticou, forcaKN: T / 1000, descidaM: comprimento - L })
        const vel = Math.hypot(v.x, v.y, v.z)
        impacto = { ...batida, quadro: quadros.length - 1, t, velocidadeMs: vel, fase }
        if (rompimento) {
          rompimento = { ...rompimento, velocidadeImpactoMs: vel }
        }
        break
      }
    }

    // parado: pendurado na vertical da ancoragem, segurando o peso, por um tempo
    if (temCorda && A && !rompeu && tEsticou !== null && t > tEsticou + TEMPO_MINIMO_ASSENTAR_S) {
      const desvio = Math.hypot(p.x - A[0], p.z - A[2])
      const emRepouso =
        desvio < DESVIO_PARADO_M && Math.hypot(v.x, v.y, v.z) < VELOCIDADE_PARADO_MS && Math.abs(T - peso) <= FORCA_NO_PESO * peso
      if (!emRepouso) paradoDesde = null
      else if (paradoDesde === null) paradoDesde = t
      else if (t - paradoDesde >= REPOUSO_SUSTENTADO_S) {
        quadros.push({ t, engate: posicao, forcaN: T, fase: 'parado' })
        assentou = true
        break
      }
    }

    if (!amortecendo && tEsticou !== null && t >= tEsticou + ATRASO_DO_AMORTECIMENTO_S && !rompeu) {
      corpo.setLinearDamping(amortecimento)
      amortecendo = true
    }
    corpo.resetForces(true)
    if (T > 0) corpo.addForce({ x: T * ux, y: T * uy, z: T * uz }, true)
    if (i < passos) mundo.step()
  }
  mundo.free()

  // a curva vai do esticar ao fim da primeira frenagem (ou ao impacto)
  const curva: CurvaDeForca | null =
    bruto.length > 1
      ? { pontos: reduzirCurva(bruto, PONTOS_NA_CURVA), duracaoS: bruto[bruto.length - 1]!.t, picoKN: Math.max(...bruto.map((b) => b.forcaKN)) }
      : null
  const ultimoQuadro = quadros[quadros.length - 1]!
  return {
    quadros,
    picoForcaN: pico,
    tEsticouS: tEsticou,
    engateAoEsticar,
    velocidadeAoEsticarMs: velocidadeAoEsticar,
    tFimDaFrenagemS: tFim,
    engateNoFimDaFrenagem: engateFim ?? (tEsticou !== null ? ultimoQuadro.engate : null),
    alongamentoNoPicoM: xNoPico,
    aberturaUsadaM: d,
    descidaAposEsticarM: tFim !== null ? descida : bruto.length > 0 ? bruto[bruto.length - 1]!.descidaM : 0,
    curva,
    impacto,
    rompimento,
    assentou,
    rigidezNPorM: k,
  }
}
